import { Fragment, useCallback, useState, useMemo } from 'react';
import {
  Button,
  Text,
  Menu,
  MenuItem,
  Portal,
  Box,
} from '@chakra-ui/react';
import {useClearRefinements, useCurrentRefinements, useRefinementList} from 'react-instantsearch-hooks-web'
import { CheckIcon } from '../../../icons';
import {CustomTooltip} from '../../../shared/components/CustomTooltip'

const CategoryFilter = ({
  variant="outline",
  text,
  id,
  icons,
  transformItems,
  attribute,
  limit,
  operator = 'and',         //if operator is 'and' then exclusion is allowed
  //allowExclusion = false,
}) => {
  const { refine, items } = useRefinementList({
    attribute,
    transformItems,
    limit,
    operator,
  });

  const { items: currentRefinements } = useCurrentRefinements();
  const { refine: clearAllRefinements } = useClearRefinements({ includedAttributes: [attribute] });

  const isActiveRefinement = useCallback(
    (attribute) => {
      return !!currentRefinements.find((ref) => ref.attribute.includes(attribute));
    },
    [currentRefinements]
  );
  const [isOpen, setIsOpen] = useState(false) 

  const active = isActiveRefinement(attribute);

  /**
   * item: {
      isNegativeSelected: boolean;
      value: string;
      label: string;
      highlighted?: string | undefined;
      count: number;
      isRefined: boolean;
    }
   */
  const handleRefine = useCallback(
    (item) => {
      if (item.isNegativeSelected) refine('-' + item.value);
      else if (item.isRefined) {
        refine(item.value);
        if (operator==="and"/**allowExclusion */) {
          refine('-' + item.value);
        }
      } else refine(item.value);
    },
    [operator/**allowExclusion */, refine]
  );

  const itemsNegative = items.filter((item) => {
    return item.label.startsWith('-');
  });

  const itemsPositive = items.filter((item) => {
    return !item.label.startsWith('-');
  });

  const itemsPositiveWithNegative = itemsPositive.map((item) => {
    return {
      ...item,
      isNegativeSelected: !!itemsNegative.find((itemNegative) => {
        return itemNegative.label.slice(1) === item.label;
      }),
    };
  });


  const memoFilters = useMemo(
    () =>
      !!itemsPositiveWithNegative && itemsPositiveWithNegative.map((item) => {
        const IconToRender = icons?.[item?.label] ? icons[item?.label] : ()=>null
        return (
          <Fragment key={item.value}>
            <CustomTooltip 
              contentProps={{
                "fontFamily":"TTCOMMONSMEDIUM",
                "bgColor":'gray.900',
                "color":'gray.400',
              }}
              content={
                item.isRefined 
                ? (operator === "and" ? "Click again to exlude from results" : "Click again to remove filter")
                : "Click to filter"
              }>
              <MenuItem
                rounded='md'
                h='36px'
                onClick={() => handleRefine(item)}
                //isDisabled={!item}
                cursor="pointer"
                color="var(--purple)"
                _hover={{
                  color: "var(--orange)"
                }}
                _icon={{fill: "var(--purple)"}}
                data-cy={`${id}-option-${item.value.replaceAll(' ', '-')}`}
              >
                {!!icons && <Box minW="15px">
                  <IconToRender width="15px" height="15px" fillColor="var(--purple)" minWidth="15px"/>
                </Box>
                }
                <Box 
                  fontFamily="TTCOMMONSMEDIUM"
                  fontSize="14px"
                  >
                  {item.label}
                </Box>
                {item.isRefined && <Box marginLeft='auto'><CheckIcon width="15px" height="15px" fillColor="var(--purple)" minWidth="15px"/></Box>}
              </MenuItem>
            </CustomTooltip>
          </Fragment>
        );
      }),
    [itemsPositiveWithNegative, handleRefine, icons]
  );

  return (
    <>
    <Menu.Root id={id} autoSelect={false} closeOnSelect={false} onOpenChange={(details)=>setIsOpen(details.open)} isLazy>
        <>
          <Menu.Trigger
            outline="none"
            asChild
          >
            <Button
                display='flex'
                alignItems='center'
                textTransform='uppercase'
                backgroundColor={!isOpen ? "white" : "var(--purple)"}
                border={active || isOpen ? "solid 2px var(--purple)" : "solid 2px var(--light-purple)"}
                height="40px"
                borderRadius="20px"
                padding="0px 20px"
                variant="plain"
                color={!isOpen ? "var(--purple)" : "white" }
                _hover={{
                  backgroundColor: "var(--purple)",
                  color: 'white',
                  borderColor: "var(--purple)"
                }}
                _active={{
                  backgroundColor: "var(--purple)",
                  color: 'white',
                  borderColor: "var(--purple)"
                }}
                overflow='hidden'
                data-cy={`${id}-button`}
              >
                <Text
                  fontSize=".8rem"
                  letterSpacing="0.128em"
                  fontFamily='TTCommonsMedium'
                > 
                {text}
                </Text>
            </Button>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content
                shadow='lg'
                py='4'
                px='3'
                overflowY='auto'
                minW={{ base: '13rem', smTo2xl: '15rem' }}
                maxH='25rem'
                borderRadius="20px"
                background="var(--light-purple)"
              >
                <Menu.Item
                  fontWeight='semibold'
                  rounded='md'
                  onClick={() => clearAllRefinements()}
                  position='relative'
                  cursor="pointer"
                  color='gray.400'
                  minH="13px"
                  _hover={{
                    color: "var(--purple)"
                  }}
                >
                  <Text position='absolute' top='0px' right='0px' fontSize='0.6875rem'>
                    Reset
                  </Text>
                </Menu.Item>
                {items && memoFilters}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </>
    </Menu.Root>
    </>
  );
};

export default CategoryFilter;